import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../../context/casl/AbilityContext";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import AccessRestricted from "../../components/AccessRestricted";
import api from "../../utils/api";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";
import { toast } from "sonner";
import {
  ArrowLeft,
  CheckCircle2,
  Clock,
  FolderOpen,
  Monitor,
  CreditCard,
  Users,
  Building2,
  CalendarDays,
  ClipboardList,
  Loader2,
  MessageSquare,
  ChevronRight,
  Smartphone,
  ShieldCheck,
  Wrench,
  StickyNote,
  User,
} from "lucide-react";

// ===================================================================
// Interfaces
// ===================================================================
interface HrFinanceTask {
  key: string;
  lead: "IT" | "HR" | "Finance";
  category: string;
  title: string;
  description: string;
  auto: boolean;
  status: "done" | "pending";
  completedAt: string | null;
  completedBy: string | null;
}

interface HrFinanceTasksData {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  startDate: string | null;
  status: string;
  progress: number;
  roleName: string | null;
  manager: { id: number; name: string } | null;
  createdBy: { id: number; name: string } | null;
  notesForItHrFinance: string | null;
  tasks: HrFinanceTask[];
}

interface WorkflowStep {
  id: string;
  name: string;
  status: "completed" | "in_progress" | "pending";
  detail: string;
}

// ===================================================================
// Helpers
// ===================================================================
const LEAD_THEME: Record<string, { label: string; icon: React.ReactNode; iconBg: string; iconColor: string; tabActive: string; tabInactive: string }> = {
  IT: {
    label: "IT",
    icon: <Monitor className="w-4 h-4" />,
    iconBg: "bg-blue-50",
    iconColor: "text-blue-600",
    tabActive: "bg-white text-gray-900 shadow-sm",
    tabInactive: "text-gray-500 hover:text-gray-700",
  },
  Finance: {
    label: "Finance",
    icon: <CreditCard className="w-4 h-4" />,
    iconBg: "bg-emerald-50",
    iconColor: "text-emerald-600",
    tabActive: "bg-white text-gray-900 shadow-sm",
    tabInactive: "text-gray-500 hover:text-gray-700",
  },
  HR: {
    label: "HR",
    icon: <Users className="w-4 h-4" />,
    iconBg: "bg-purple-50",
    iconColor: "text-purple-600",
    tabActive: "bg-white text-gray-900 shadow-sm",
    tabInactive: "text-gray-500 hover:text-gray-700",
  },
};

// Category icon + colour mapping (matches the Onboarding Settings
// requirement category icons where the names line up).
const getCategoryStyle = (category: string) => {
  const map: Record<string, { icon: React.ReactNode; bg: string; color: string }> = {
    "SharePoint Groups": { icon: <FolderOpen className="w-4 h-4" />, bg: "bg-blue-50", color: "text-blue-600" },
    "MS Teams / Group Chats": { icon: <MessageSquare className="w-4 h-4" />, bg: "bg-purple-50", color: "text-purple-600" },
    "IT Equipment": { icon: <Monitor className="w-4 h-4" />, bg: "bg-blue-50", color: "text-blue-600" },
    "Software Access": { icon: <Monitor className="w-4 h-4" />, bg: "bg-purple-50", color: "text-purple-600" },
    "System Access": { icon: <ShieldCheck className="w-4 h-4" />, bg: "bg-emerald-50", color: "text-emerald-600" },
    Tools: { icon: <Smartphone className="w-4 h-4" />, bg: "bg-emerald-50", color: "text-emerald-600" },
    Security: { icon: <ShieldCheck className="w-4 h-4" />, bg: "bg-amber-50", color: "text-amber-600" },
    "Security Access": { icon: <ShieldCheck className="w-4 h-4" />, bg: "bg-amber-50", color: "text-amber-600" },
    "Payroll & Finance": { icon: <CreditCard className="w-4 h-4" />, bg: "bg-emerald-50", color: "text-emerald-600" },
    "HR Coordination": { icon: <Users className="w-4 h-4" />, bg: "bg-purple-50", color: "text-purple-600" },
  };
  return map[category] || { icon: <Wrench className="w-4 h-4" />, bg: "bg-gray-50", color: "text-gray-600" };
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return "Not set";
  return new Date(dateStr).toLocaleDateString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

// Tasks that mirror the New Hire Form's Setup & Access step: SharePoint /
// MS Teams groups plus the requirement items selected at Commence Hiring
// (including the legacy fixed buckets for pre-requirement requests).
const isProvisioningTask = (task: HrFinanceTask) =>
  task.key.startsWith("req:") || task.key.startsWith("legacy:") || task.key.startsWith("setup:");

// ===================================================================
// Main Component
// ===================================================================
const OnboardingHrFinanceTasks: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const ability = useAbility(AbilityContext);

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<HrFinanceTasksData | null>(null);
  const [activeTab, setActiveTab] = useState("overview");
  const [togglingKey, setTogglingKey] = useState<string | null>(null);
  const [activeSetupGroup, setActiveSetupGroup] = useState(0);
  const setupSectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  const canView = ability.can(ACTIONS.READ, RESOURCES.ONBOARDING_REQUEST_DETAIL);

  const canUpdateLead = (lead: string) => {
    const resource =
      lead === "IT"
        ? RESOURCES.ONBOARDING_IT_STEP
        : lead === "HR"
        ? RESOURCES.ONBOARDING_HR_STEP
        : RESOURCES.ONBOARDING_FINANCE_STEP;
    return ability.can(ACTIONS.UPDATE, resource);
  };

  const fetchTasks = useCallback(
    async ({ silent = false }: { silent?: boolean } = {}) => {
      if (!id) return;
      try {
        if (!silent) setLoading(true);
        const res = await api.get<{ success: boolean; data: HrFinanceTasksData }>(
          `/api/onboarding/${id}/hr-finance-tasks`
        );
        if (res.success && res.data) {
          setData(res.data);
        } else if (!silent) {
          toast.error("Failed to load HR & Finance tasks");
        }
      } catch (error) {
        console.error("Error fetching HR & Finance tasks:", error);
        if (!silent) toast.error("Failed to load HR & Finance tasks");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [id]
  );

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // Scroll spy: keep the Setup & Access category quick-nav highlighting
  // the category section currently in view.
  useEffect(() => {
    if (activeTab !== "it") return;
    const handleScroll = () => {
      let active = 0;
      for (let i = 0; i < setupSectionRefs.current.length; i++) {
        const el = setupSectionRefs.current[i];
        if (el && el.getBoundingClientRect().top <= 170) active = i;
      }
      setActiveSetupGroup(active);
    };
    // The app scrolls inside <main>, not the window — capture-phase
    // document listener catches scroll events from any scroll container.
    document.addEventListener("scroll", handleScroll, { passive: true, capture: true });
    handleScroll();
    return () => document.removeEventListener("scroll", handleScroll, { capture: true });
  }, [activeTab, data]);

  const handleToggle = async (task: HrFinanceTask, done: boolean) => {
    if (!id || togglingKey) return;
    setTogglingKey(task.key);
    try {
      const res = await api.put<{
        success: boolean;
        message?: string;
        data: { key: string; done: boolean; completedAt: string | null };
      }>(`/api/onboarding/${id}/hr-finance-tasks/${encodeURIComponent(task.key)}`, {
        done,
        lead: task.lead,
      });
      if (res.success && res.data) {
        // Refetch so completed-by names and auto states stay canonical.
        await fetchTasks({ silent: true });
      } else {
        toast.error(res.message || "Failed to update task");
      }
    } catch (error) {
      console.error("Error updating task:", error);
      toast.error("Failed to update task");
    } finally {
      setTogglingKey(null);
    }
  };

  // -----------------------------------------------------------------
  // Derived state
  // -----------------------------------------------------------------
  const tasks = data?.tasks ?? [];

  const tasksByLead = (lead: string) => tasks.filter((t) => t.lead === lead);
  const doneInLead = (lead: string) => tasksByLead(lead).filter((t) => t.status === "done").length;
  const totalInLead = (lead: string) => tasksByLead(lead).length;
  const leadComplete = (lead: string) => {
    const list = tasksByLead(lead);
    return list.length > 0 && list.every((t) => t.status === "done");
  };
  const leadStarted = (lead: string) => tasksByLead(lead).some((t) => t.status === "done");

  // The Setup & Access Tasks tab mirrors the New Hire Form's Setup &
  // Access step, so it shows every provisioning item regardless of lead.
  const provisioningTasks = tasks.filter(isProvisioningTask);
  const doneSetup = provisioningTasks.filter((t) => t.status === "done").length;
  const totalSetup = provisioningTasks.length;
  // Team tabs (HR / Finance) only carry their own team tasks; provisioning
  // items now live exclusively on the Setup & Access tab.
  const teamTasks = (lead: string) => tasksByLead(lead).filter((t) => !isProvisioningTask(t));

  const payrollReady = tasks.some((t) => t.key === "finance:payroll-setup-complete" && t.status === "done");
  const converted = data?.status === "CONVERTED";

  const workflowSteps: WorkflowStep[] = [
    { id: "request", name: "Request Created", status: "completed", detail: "Onboarding record created from Commence Hiring" },
    { id: "it", name: "IT Setup", status: leadComplete("IT") ? "completed" : leadStarted("IT") ? "in_progress" : "pending", detail: "Equipment and access provisioning" },
    { id: "hr", name: "HR Setup", status: leadComplete("HR") ? "completed" : leadStarted("HR") ? "in_progress" : "pending", detail: "Coordination and first-day logistics" },
    { id: "finance", name: "Finance Setup", status: leadComplete("Finance") ? "completed" : leadStarted("Finance") ? "in_progress" : "pending", detail: "Payroll and banking setup" },
    { id: "payroll", name: "Payroll Ready", status: payrollReady ? "completed" : "pending", detail: "Payroll signed off before first day" },
    { id: "converted", name: "Converted to Active User", status: converted ? "completed" : "pending", detail: "Hire gains full system access" },
  ];

  const doneCount = tasks.filter((t) => t.status === "done").length;
  const overallPercent = tasks.length > 0 ? Math.round((doneCount / tasks.length) * 100) : 0;

  // -----------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------
  if (!canView) {
    return <AccessRestricted />;
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-gray-600" />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-gray-500">
        <p>Onboarding request not found</p>
        <button
          className="mt-4 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          onClick={() => navigate("/onboarding")}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  const fullName = `${data.firstName} ${data.lastName}`.trim();

  const getStatusBadge = (status: string) => {
    const map: Record<string, { label: string; className: string }> = {
      NOT_STARTED: { label: "Not Started", className: "bg-gray-100 text-gray-700 border-gray-200" },
      IN_PROGRESS: { label: "Pre-Start In Progress", className: "bg-blue-50 text-blue-700 border-blue-200" },
      COMPLETED: { label: "Completed", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      CONVERTED: { label: "Converted to Active User", className: "bg-emerald-50 text-emerald-700 border-emerald-200" },
      CANCELLED: { label: "Cancelled", className: "bg-red-50 text-red-700 border-red-200" },
    };
    const s = map[status] || { label: status, className: "bg-gray-100 text-gray-700" };
    return <Badge variant="outline" className={`${s.className} font-medium`}>{s.label}</Badge>;
  };

  const TABS = [
    { id: "overview", label: "Workflow Overview", icon: <ClipboardList className="w-4 h-4" /> },
    { id: "it", label: "Setup & Access Tasks", icon: LEAD_THEME.IT.icon, count: totalSetup },
    { id: "finance", label: "Finance Tasks", icon: LEAD_THEME.Finance.icon, count: teamTasks("Finance").length },
    { id: "hr", label: "HR Tasks", icon: LEAD_THEME.HR.icon, count: teamTasks("HR").length },
  ];

  const renderChecklistItem = (task: HrFinanceTask) => {
    const canUpdate = canUpdateLead(task.lead);
    const isToggling = togglingKey === task.key;
    const done = task.status === "done";
    return (
      <div
        key={task.key}
        className={`flex items-start gap-3 p-3.5 rounded-xl border transition-colors ${
          done ? "bg-emerald-50/50 border-emerald-100" : "bg-white border-gray-200"
        }`}
      >
        {task.auto ? (
          done ? (
            <div className="w-5 h-5 rounded-full bg-emerald-500 flex items-center justify-center shrink-0 mt-0.5">
              <CheckCircle2 className="w-3.5 h-3.5 text-white" />
            </div>
          ) : (
            <div className="w-5 h-5 rounded-full border-2 border-gray-300 bg-white shrink-0 mt-0.5 flex items-center justify-center">
              <Clock className="w-3 h-3 text-gray-400" />
            </div>
          )
        ) : (
          <Checkbox
            id={`task-${task.key}`}
            checked={done}
            disabled={!canUpdate || isToggling}
            onCheckedChange={(checked) => handleToggle(task, checked === true)}
            className={`mt-0.5 shrink-0 ${done ? "bg-emerald-500 border-emerald-500" : ""}`}
          />
        )}
        <label
          htmlFor={task.auto ? undefined : `task-${task.key}`}
          className={`flex-1 min-w-0 ${task.auto ? "" : "cursor-pointer"}`}
        >
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`text-sm font-medium ${done ? "text-gray-500 line-through" : "text-gray-900"}`}>
              {task.title}
            </span>
            {task.auto && (
              <Badge variant="outline" className="bg-gray-50 text-gray-500 border-gray-200 text-[10px] font-medium">
                Auto
              </Badge>
            )}
            {done && (
              <Badge
                variant="outline"
                className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-medium"
              >
                Done
              </Badge>
            )}
          </div>
          {task.description && (
            <p className="text-xs text-gray-500 mt-0.5">{task.description}</p>
          )}
          {done && task.completedBy && (
            <p className="text-xs text-gray-400 mt-1">
              {task.completedBy}
              {task.completedAt && ` · ${formatDate(task.completedAt)}`}
            </p>
          )}
        </label>
      </div>
    );
  };

  const ChecklistHeaderCard: React.FC<{
    icon: React.ReactNode;
    iconBg: string;
    iconColor: string;
    title: string;
    subtitle: string;
    done: number;
    total: number;
  }> = ({ icon, iconBg, iconColor, title, subtitle, done, total }) => {
    const pct = total > 0 ? Math.round((done / total) * 100) : 0;
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className={`w-10 h-10 rounded-lg ${iconBg} flex items-center justify-center shrink-0`}>
            <div className={iconColor}>{icon}</div>
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-500 mt-0.5">{subtitle}</p>
          </div>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xl font-bold text-gray-900">{pct}%</p>
          <p className="text-xs text-gray-500">
            {done}/{total} done
          </p>
        </div>
      </div>
    );
  };

  const renderLeadTab = (lead: "HR" | "Finance", title: string, subtitle: string) => {
    const leadTasks = teamTasks(lead);
    const done = leadTasks.filter((t) => t.status === "done").length;
    const total = leadTasks.length;
    const theme = LEAD_THEME[lead];

    const groups: { category: string; tasks: HrFinanceTask[] }[] = [];
    for (const task of leadTasks) {
      const existing = groups.find((g) => g.category === task.category);
      if (existing) existing.tasks.push(task);
      else groups.push({ category: task.category, tasks: [task] });
    }

    return (
      <div className="space-y-4">
        {/* Checklist header */}
        <ChecklistHeaderCard
          icon={theme.icon}
          iconBg={theme.iconBg}
          iconColor={theme.iconColor}
          title={title}
          subtitle={subtitle}
          done={done}
          total={total}
        />

        {total === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <ClipboardList className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">No {theme.label} tasks</h3>
            <p className="text-xs text-gray-500">
              No {theme.label.toLowerCase()} tasks found for this hire.
            </p>
          </div>
        ) : (
          groups.map((group) => {
            const style = getCategoryStyle(group.category);
            return (
              <div key={group.category} className="bg-white rounded-xl border border-gray-200 p-5">
                <div className="flex items-center gap-2 mb-3">
                  <div className={`w-7 h-7 rounded-lg ${style.bg} flex items-center justify-center`}>
                    <div className={style.color}>{style.icon}</div>
                  </div>
                  <h3 className="text-sm font-semibold text-gray-900">{group.category}</h3>
                </div>
                <div className="space-y-2.5">
                  {group.tasks.map((task) => renderChecklistItem(task))}
                </div>
              </div>
            );
          })
        )}
      </div>
    );
  };

  // Setup & Access Tasks tab — mirrors the New Hire Form's Setup & Access
  // step: SharePoint / Teams groups first (form order), then the
  // requirement categories, each with a clickable quick-nav chip.
  const renderSetupAccessTab = () => {
    const theme = LEAD_THEME.IT;
    const done = doneSetup;
    const total = totalSetup;

    const groups: { category: string; tasks: HrFinanceTask[] }[] = [];
    for (const task of provisioningTasks) {
      const existing = groups.find((g) => g.category === task.category);
      if (existing) existing.tasks.push(task);
      else groups.push({ category: task.category, tasks: [task] });
    }
    const SETUP_GROUP_PRIORITY = ["SharePoint Groups", "MS Teams / Group Chats"];
    groups.sort((a, b) => {
      const ai = SETUP_GROUP_PRIORITY.indexOf(a.category);
      const bi = SETUP_GROUP_PRIORITY.indexOf(b.category);
      if (ai !== -1 || bi !== -1) {
        return (ai === -1 ? SETUP_GROUP_PRIORITY.length : ai) - (bi === -1 ? SETUP_GROUP_PRIORITY.length : bi);
      }
      return 0;
    });

    const scrollToGroup = (index: number) => {
      setActiveSetupGroup(index);
      setupSectionRefs.current[index]?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
      <div className="space-y-4">
        <ChecklistHeaderCard
          icon={theme.icon}
          iconBg={theme.iconBg}
          iconColor={theme.iconColor}
          title="Setup & Access Checklist"
          subtitle={`Equipment and access for ${fullName} starting ${formatDate(data.startDate)}`}
          done={done}
          total={total}
        />

        {total === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
            <Wrench className="w-10 h-10 text-gray-300 mx-auto mb-3" />
            <h3 className="text-sm font-medium text-gray-900 mb-1">No setup &amp; access items</h3>
            <p className="text-xs text-gray-500">
              No equipment, software or access items were selected for this hire.
            </p>
          </div>
        ) : (
          <>
            {/* Category quick-nav */}
            <div className="sticky top-16 lg:top-2 z-10">
              <div className="bg-gray-100 p-1 rounded-xl flex items-center gap-1 overflow-x-auto">
                {groups.map((group, index) => {
                  const groupDone = group.tasks.filter((t) => t.status === "done").length;
                  return (
                    <button
                      key={group.category}
                      onClick={() => scrollToGroup(index)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                        activeSetupGroup === index
                          ? "bg-white text-gray-900 shadow-sm"
                          : "text-gray-500 hover:text-gray-700"
                      }`}
                    >
                      {group.category}
                      <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                        {groupDone}/{group.tasks.length}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Category sections */}
            {groups.map((group, index) => {
              const style = getCategoryStyle(group.category);
              return (
                <div
                  key={group.category}
                  ref={(el) => {
                    setupSectionRefs.current[index] = el;
                  }}
                  className="bg-white rounded-xl border border-gray-200 p-5 scroll-mt-28 lg:scroll-mt-16"
                >
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-7 h-7 rounded-lg ${style.bg} flex items-center justify-center`}>
                      <div className={style.color}>{style.icon}</div>
                    </div>
                    <h3 className="text-sm font-semibold text-gray-900">{group.category}</h3>
                  </div>
                  <div className="space-y-2.5">
                    {group.tasks.map((task) => renderChecklistItem(task))}
                  </div>
                </div>
              );
            })}
          </>
        )}
      </div>
    );
  };

  return (
    <div className="flex flex-col space-y-6 animate-in fade-in duration-500 pb-10">
      {/* Breadcrumb + Back */}
      <div className="flex items-center gap-2 text-sm text-gray-500">
        <button
          onClick={() => navigate(`/onboarding/${data.id}`)}
          className="flex items-center gap-1 hover:text-gray-900 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" />
          Onboarding
        </button>
        <ChevronRight className="w-3 h-3" />
        <span className="text-gray-900 font-medium">{fullName}</span>
        <ChevronRight className="w-3 h-3" />
        <span className="text-gray-900 font-medium">HR & Finance Tasks</span>
      </div>

      {/* Title + Status */}
      <div className="flex items-center justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">HR & Finance Tasks</h1>
          <p className="text-sm text-gray-500 mt-1">
            {fullName}
            {data.roleName && ` · ${data.roleName}`}
          </p>
        </div>
        {getStatusBadge(data.status)}
      </div>

      {/* Overall Progress */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Overall Progress</h2>
            <p className="text-sm text-gray-500">
              {doneCount} of {tasks.length} provisioning tasks complete
            </p>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-gray-900">{overallPercent}%</p>
            <p className="text-xs text-gray-500">Complete</p>
          </div>
        </div>
        <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
          <div
            className="h-full bg-gray-900 rounded-full transition-all"
            style={{ width: `${overallPercent}%` }}
          />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1 bg-gray-100 p-1 rounded-xl overflow-x-auto">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all flex items-center gap-2 ${
              activeTab === tab.id ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
            }`}
          >
            {tab.icon}
            {tab.label}
            {tab.count !== undefined && tab.count > 0 && (
              <span className="ml-0.5 text-xs px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">
                {tab.count}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* ===== WORKFLOW OVERVIEW TAB ===== */}
      {activeTab === "overview" && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left: Workflow Progress */}
          <div className="lg:col-span-2 space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900">Workflow Progress</h2>
              <p className="text-sm text-gray-500 mt-1">
                Track progress through each stage of the onboarding process
              </p>

              <div className="mt-6 space-y-3">
                {workflowSteps.map((step) => (
                  <div
                    key={step.id}
                    className={`rounded-xl border p-4 ${
                      step.status === "completed"
                        ? "bg-emerald-50/50 border-emerald-200"
                        : step.status === "in_progress"
                        ? "bg-blue-50/50 border-blue-200"
                        : "bg-gray-50/50 border-gray-200"
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      {step.status === "completed" ? (
                        <CheckCircle2 className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
                      ) : step.status === "in_progress" ? (
                        <Clock className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                      ) : (
                        <div className="w-5 h-5 rounded-full border-2 border-gray-300 shrink-0 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900">{step.name}</span>
                          {step.status === "completed" && (
                            <Badge
                              variant="outline"
                              className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] font-medium"
                            >
                              Done
                            </Badge>
                          )}
                          {step.status === "in_progress" && (
                            <Badge
                              variant="outline"
                              className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] font-medium"
                            >
                              In Progress
                            </Badge>
                          )}
                        </div>
                        <p className="text-xs text-gray-500 mt-0.5">{step.detail}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Request Details */}
          <div className="space-y-6">
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-base font-semibold text-gray-900 mb-4">Request Details</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <User className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-gray-900">{fullName}</p>
                    <p className="text-xs text-gray-500">{data.email}</p>
                  </div>
                </div>
                {data.roleName && (
                  <div className="flex items-start gap-3">
                    <Building2 className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-700">{data.roleName}</p>
                    </div>
                  </div>
                )}
                <div className="flex items-start gap-3">
                  <CalendarDays className="w-4 h-4 text-gray-400 mt-0.5" />
                  <div>
                    <p className="text-sm text-gray-700">Start: {formatDate(data.startDate)}</p>
                  </div>
                </div>
                {data.manager && (
                  <div className="flex items-start gap-3">
                    <Users className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-700">Manager: {data.manager.name}</p>
                    </div>
                  </div>
                )}
                {data.createdBy && (
                  <div className="flex items-start gap-3">
                    <ClipboardList className="w-4 h-4 text-gray-400 mt-0.5" />
                    <div>
                      <p className="text-sm text-gray-700">Requested by: {data.createdBy.name}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Notes for IT / HR / Finance (captured at Commence Hiring) */}
              {data.notesForItHrFinance && (
                <div className="mt-6 pt-4 border-t border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <StickyNote className="w-4 h-4 text-amber-500" />
                    <h3 className="text-sm font-semibold text-gray-900">Notes for IT / HR / Finance</h3>
                  </div>
                  <p className="text-sm text-gray-600 bg-amber-50 rounded-lg p-3 border border-amber-100">
                    {data.notesForItHrFinance}
                  </p>
                </div>
              )}
            </div>

            {/* Lead summaries */}
            <div className="space-y-3">
              {(["IT", "HR", "Finance"] as const).map((lead) => {
                const theme = LEAD_THEME[lead];
                const done = doneInLead(lead);
                const total = totalInLead(lead);
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                return (
                  <button
                    key={lead}
                    onClick={() => setActiveTab(lead === "IT" ? "it" : lead === "HR" ? "hr" : "finance")}
                    className="w-full bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between hover:shadow-sm transition-all text-left"
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-lg ${theme.iconBg} flex items-center justify-center`}>
                        <div className={theme.iconColor}>{theme.icon}</div>
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{theme.label} Tasks</p>
                        <p className="text-xs text-gray-500">
                          {done}/{total} done
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-gray-900">{pct}%</span>
                      <ChevronRight className="w-4 h-4 text-gray-300" />
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ===== SETUP & ACCESS TASKS TAB ===== */}
      {activeTab === "it" && renderSetupAccessTab()}

      {/* ===== FINANCE TASKS TAB ===== */}
      {activeTab === "finance" && (
        <div className="space-y-4">
          {renderLeadTab(
            "Finance",
            "Finance & Payroll Checklist",
            `Payroll setup for ${fullName} starting ${formatDate(data.startDate)}`
          )}

          {/* Employee Payroll Information */}
          <div className="bg-white rounded-xl border border-gray-200 p-5">
            <h3 className="text-sm font-semibold text-gray-900 mb-4">Employee Payroll Information</h3>
            <div className="space-y-3">
              {[
                { label: "Full Name", value: fullName },
                { label: "Position", value: data.roleName || "—" },
                { label: "Start Date", value: formatDate(data.startDate) },
                { label: "Payroll Status", value: payrollReady ? "Setup Complete" : "Pending Setup" },
              ].map((item) => (
                <div key={item.label} className="flex items-center justify-between text-sm">
                  <span className="text-gray-500">{item.label}</span>
                  <span className="text-gray-900 font-medium">{item.value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ===== HR TASKS TAB ===== */}
      {activeTab === "hr" && (
        renderLeadTab(
          "HR",
          "HR Coordinator Checklist",
          `HR coordination tasks for ${fullName}`
        )
      )}
    </div>
  );
};

export default OnboardingHrFinanceTasks;
